import { Badge } from "@/modern/components/ui/badge";
import { CardTitle } from "@/modern/components/ui/card";
import { useCallback, useEffect } from "react";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { NotesButtonGroup } from "@/react/components/entityViews/notes/components/NotesMenu";
import { EntityNotesProvider } from "@/react/components/entityViews/notes/context/EntityNotesContext";
import { NotesModalProvider } from "@/react/components/entityViews/notes/context/NotesModalContext";
import { useNotesModal } from "@/react/hooks/entityHooks/useNotesModal";
import { useEntityNotesView } from "@/react/hooks/entityHooks/useEntityNotesView";
import { EmptyNotesState } from "./components/EmptyNotesState";
import { NotesEditorArea } from "./components/NotesEditorArea";
import { NotesList } from "./components/NotesList";
import { useEntityManagerContext } from "@/react/components/entityManager/context/EntityManagerContext";

export const EntityNotesView = () => {
  return (
    <EntityNotesProvider>
      <NotesModalProvider>
        <EntityNotesViewContent />
      </NotesModalProvider>
    </EntityNotesProvider>
  );
};

const EntityNotesViewContent = () => {
  const { otherParams } = useEntityManagerContext();
  const {
    richEditorRef,
    activeNote,
    readOnly,
    setReadonly,
    notes,
    loading,
    hasChanges,
    isEmpty,
    entityRecord,
    handleSave,
    handleCancel,
    handleDeleteNote,
    setActiveNoteData,
    hasNotes
  } = useEntityNotesView();

  const { handleCreateNewNote, openNewNoteModal, openEditNoteModal } = useNotesModal({
    record: entityRecord,
    noteRecord: activeNote,
    isEditor: false,
    onOpenNewNote: () => setReadonly(true)
  });

  const handleOpenCreateNote = useCallback(() => {
    handleCreateNewNote();
    openNewNoteModal();
  }, [handleCreateNewNote, openNewNoteModal]);

  useEffect(() => {
    if (otherParams && otherParams.editNote && notes.length) {
      const noteRecord = notes.find((note) => note.id === otherParams.editNote);
      if (noteRecord) {
        setActiveNoteData(noteRecord);
        openEditNoteModal();
      }
    }
  }, [otherParams, notes])

  return (
    <DashboardCard
      className="h-auto min-h-[30rem]"
      header={
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-base relative">
            Notes
            {notes.length > 0 && (
              <Badge variant="muted" size="xs" className="absolute -right-7 -top-2">
                {notes.length}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <NotesButtonGroup
              noteRecord={activeNote}
              isEditor={false}
              onDeleteNote={handleDeleteNote}
              onOpenNewNote={() => setReadonly(true)}
              activeNote={activeNote}
              readOnly={readOnly}
              setReadonly={setReadonly}
              handleSave={handleSave}
              handleCancel={handleCancel}
              hasChanges={hasChanges}
            />
          </div>
        </div>
      }
      cardContentClassName="flex-1 !p-0 overflow-hidden relative"
    >
      {!hasNotes && <EmptyNotesState onCreateNote={handleOpenCreateNote} loading={loading} />}

      {hasNotes && (
        <div className="grid grid-cols-[70%_30%]">
          <NotesEditorArea richEditorRef={richEditorRef} loading={loading} readOnly={readOnly} isEmpty={isEmpty} hasActiveNote={Boolean(activeNote)} />
          <NotesList notes={notes} activeNoteId={activeNote?.id || null} onSelectNote={setActiveNoteData} />
        </div>
      )}
    </DashboardCard>
  );
};
